import React from 'react'
import {
  emptyStateSearchGraphic,
  emptyStateFolderGraphic,
  emptyStatePassGraphic,
  emptyStateSecurityGraphic,
  emptyStateIncorrectGraphic,
  emptyStateProcessingGraphic
} from '@workday/canvas-graphics-web'
import { Msg } from '~/common/localizationUtils'
import {
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  Stack,
  StackProps,
  Collapse,
  Text,
  TextProps,
  Flex,
  forwardRef,
  useDisclosure
} from '../../chakra'
import { isDevelopment } from '~/components/jsonschema/VndlyJsonSchemaFormBase/actions/utils'

// Abstract component for rendering an SVG string
const EmptyStateImage = ({ svgContents, ...props }: { svgContents: string } & BoxProps) => (
  <Box
    role="img"
    data-empty-image
    aria-hidden
    alt=""
    sx={{
      '& svg': { width: '100%', height: '100%' }
    }}
    {...props}
    dangerouslySetInnerHTML={{ __html: svgContents }}
  />
)

type EmptyStateProps = {
  title?: React.ReactNode
} & Omit<TextProps, 'title'>

export const CaughtUpGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStatePassGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const CaughtUp = ({ title, ...props }: EmptyStateProps) => (
  <>
    <CaughtUpGraphic />
    <Text textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_caught_up" />}
    </Text>
  </>
)

CaughtUp.displayName = 'CaughtUp'

export const NothingToShowGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateFolderGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const NothingToShow = ({ title, ...props }: EmptyStateProps) => (
  <>
    <NothingToShowGraphic />
    <Text textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_nothing_to_show" />}
    </Text>
  </>
)

NothingToShow.displayName = 'NothingToShow'

export const NoResultsGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateSearchGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const NoResults = ({ title, ...props }: EmptyStateProps) => (
  <>
    <NoResultsGraphic />
    <Text textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_no_search_results" />}
    </Text>
  </>
)

NoResults.displayName = 'NoResults'

export const LockedOutGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateSecurityGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const LockedOut = ({ title, ...props }: EmptyStateProps) => (
  <>
    <LockedOutGraphic />
    <Text textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_locked_out" />}
    </Text>
  </>
)

LockedOut.displayName = 'LockedOut'

export const RefreshErrorGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateProcessingGraphic.svg} />
)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const RefreshError = ({
  title,
  error,
  onRefresh,
  ...props
}: EmptyStateProps & {
  error?: string | Error | null // Allow string or Error object
  onRefresh?: () => void // Optional refresh handler
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: Boolean(isDevelopment() && error) })
  return (
    <>
      <RefreshErrorGraphic />
      <Text textStyle="bodySemi" {...props}>
        {title ?? <Msg id="common.ui.error_processing_request_try_again" />}
      </Text>
      <ButtonGroup justifyContent="center">
        {onRefresh && (
          <Button variant="link" onClick={onRefresh}>
            <Msg id="common.ui.refresh" />
          </Button>
        )}
        {isDevelopment() && error && (
          <Button variant="link" onClick={onToggle}>
            <Msg id="term.details" />
          </Button>
        )}
      </ButtonGroup>
      <Collapse in={isOpen}>
        <Stack as="pre" whiteSpace="pre-wrap" w="full" bg="gray.100" p={3} fontSize="sm" mt={4} textAlign="left">
          {typeof error === 'string' ? (
            error
          ) : (
            <>
              <Box>{error?.message}</Box>
              <Box>{error?.stack}</Box>
            </>
          )}
        </Stack>
      </Collapse>
    </>
  )
}

RefreshError.displayName = 'RefreshError'

export const BrowserErrorGraphic = (props: BoxProps) => (
  <EmptyStateImage {...props} svgContents={emptyStateIncorrectGraphic.svg} />
)
/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const BrowserError = ({ title, ...props }: EmptyStateProps) => (
  <>
    <BrowserErrorGraphic />
    <Text textStyle="bodySemi" {...props}>
      {title ?? <Msg id="common.ui.empty_state_browser_error" />}
    </Text>
  </>
)

BrowserError.displayName = 'BrowserError'

/**
 * A component that provides a consistent layout for empty states across the application.
 *
 * You'll need to nest the specific empty state graphic and text inside this component.
 *
 * @example
 * ```
 * <EmptyState>
 *   <EmptyState.CaughtUp title="An Overridden Title" />
 *   <Text>Optional Description</Text>
 *   <Link variant="link" href="#">
 *     Optional Link
 *   </Link>
 * </EmptyState>
 * ```
 * @see: https://storybook.beta.vndly.com/?path=/docs/components-empty-states--docs
 */
export const EmptyState = Object.assign(
  forwardRef(({ children, ...props }: StackProps, ref) => {
    return (
      <Box
        data-empty-state-container
        sx={{
          containerType: 'inline-size',
          containerName: 'empty-state-container',
          '@container empty-state-container (min-width: 0px)': {
            '[data-container]': { p: 6 },
            '[data-empty-image]': { display: 'none' }
          },
          '@container empty-state-container (min-width: 480px)': {
            '[data-container]': { p: 12, '&>*~*': { mt: '8px' } },
            '[data-empty-image]': { display: 'block', width: '360px', height: '180px' }
          },
          '@container empty-state-container (min-width: 1080px)': {
            '[data-empty-image]': { width: '480px', height: '240px' }
          }
        }}>
        <Flex
          data-container
          textAlign="center"
          flexDirection="column"
          alignItems="center"
          role="alert"
          ref={ref}
          {...props}>
          {children}
        </Flex>
      </Box>
    )
  }),
  {
    CaughtUp,
    NothingToShow,
    NoResults,
    LockedOut,
    RefreshError,
    BrowserError
  }
)

EmptyState.displayName = 'EmptyState'
